import type { Article } from "./types";

export const article: Article = {
  slug: "phone-addiction-bible-verses",
  title: "12 Bible verses for phone addiction, and how to use them",
  h1: "Bible verses for phone addiction",
  description:
    "Twelve verses that speak to the pull of the phone — attention, appetite, rest and the use of a life — with a short note on what each one is actually for.",
  targets: [
    "bible verses about phone addiction",
    "scripture for social media addiction",
    "bible verses about self control technology",
    "what does the bible say about screen time",
  ],
  published: "2026-08-17",
  standfirst:
    "Scripture never mentions a phone. It has a great deal to say about attention, appetite and what an hour is for.",
  takeaways: [
    "Scripture never mentions phones, but it addresses the four things underneath the habit: attention, appetite, rest and the use of an hour.",
    "1 Corinthians 6:12 is the most useful passage here — it does not ask whether the phone is permitted, only whether you are under its power.",
    "Much night scrolling is tiredness rather than appetite; Psalm 127:2 and Matthew 11:28 speak to that more accurately than a self-control verse would.",
    "Pick one verse, attach it to the moment the habit fires, and let it arrive when you are not looking for it.",
  ],
  body: [
    {
      type: "p",
      text: "Searching for verses about phone addiction turns up the same handful of quotations, usually stripped of context and stacked into a graphic. That is not much use at 11pm with the phone in your hand. What follows is twelve passages grouped by what they are actually for — because a verse about self-control does a different job from a verse about rest, and reaching for the wrong one is why this kind of list often bounces off.",
    },
    {
      type: "note",
      text: "Quotations are from the World English Bible, which is in the public domain.",
    },
    {
      type: "h2",
      id: "attention",
      text: "When the problem is attention",
    },
    {
      type: "p",
      text: "Most phone use is not a moral failure. It is attention going somewhere on its own, repeatedly, until the going is automatic. These are the passages about where attention is pointed.",
    },
    {
      type: "verse",
      verse: {
        text: "Be still, and know that I am God.",
        ref: "Psalm 46:10",
      },
    },
    {
      type: "p",
      text: "The shortest and the hardest. Stillness is not the absence of noise; it is the decision to stop filling a gap. Use this one at the moment you reach for the phone without having decided to.",
    },
    {
      type: "verse",
      verse: {
        text: "Let your eyes look straight ahead. Fix your gaze directly before you.",
        ref: "Proverbs 4:25",
      },
    },
    {
      type: "p",
      text: "A verse about direction rather than restraint. The feed is designed to be a sequence of things that are not what you were doing, and this is the answer to that specifically.",
    },
    {
      type: "verse",
      verse: {
        text: "Set your mind on the things that are above, not on the things that are on the earth.",
        ref: "Colossians 3:2",
      },
    },
    {
      type: "verse",
      verse: {
        text: "Whatever things are true, whatever things are honorable, whatever things are just, whatever things are pure, whatever things are lovely, whatever things are of good report … think about these things.",
        ref: "Philippians 4:8",
      },
    },
    {
      type: "p",
      text: "Worth reading as a filter rather than as an instruction. Held against the last twenty minutes of scrolling, it is uncomfortably specific.",
    },
    {
      type: "h2",
      id: "appetite",
      text: "When the problem is appetite",
    },
    {
      type: "p",
      text: "Some evenings the issue is not drifting attention but wanting — the compulsive open, the refresh, the third check in five minutes. Scripture treats appetite gently and seriously, and never as something to be despised.",
    },
    {
      type: "verse",
      verse: {
        text: "“All things are lawful for me,” but not all things are expedient. “All things are lawful for me,” but I will not be brought under the power of anything.",
        ref: "1 Corinthians 6:12",
      },
    },
    {
      type: "p",
      text: "The most useful verse in this entire list, because it does not require you to decide that your phone is evil. The question it asks is only whether you are under its power. That is a question you can answer honestly.",
    },
    {
      type: "verse",
      verse: {
        text: "Like a city that is broken down and without walls is a man whose spirit is without restraint.",
        ref: "Proverbs 25:28",
      },
    },
    {
      type: "verse",
      verse: {
        text: "But the fruit of the Spirit is love, joy, peace, patience, kindness, goodness, faith, gentleness, and self-control.",
        ref: "Galatians 5:22–23",
      },
    },
    {
      type: "p",
      text: "Note where self-control appears: at the end of a list of things that grow, not at the start of a list of things you manufacture. That ordering is the difference between a practice and a New Year's resolution.",
    },
    {
      type: "h2",
      id: "rest",
      text: "When the problem is rest",
    },
    {
      type: "p",
      text: "A great deal of night scrolling is not appetite at all. It is tiredness with nowhere to put itself.",
    },
    {
      type: "verse",
      verse: {
        text: "Come to me, all you who labor and are heavily burdened, and I will give you rest.",
        ref: "Matthew 11:28",
      },
    },
    {
      type: "verse",
      verse: {
        text: "In peace I will both lay myself down and sleep, for you alone, Yahweh, make me live in safety.",
        ref: "Psalm 4:8",
      },
    },
    {
      type: "verse",
      verse: {
        text: "It is vain for you to rise up early, to stay up late, eating the bread of toil; for he gives sleep to his loved ones.",
        ref: "Psalm 127:2",
      },
    },
    {
      type: "p",
      text: "If one verse belongs on the lock screen of a person who scrolls until 1am, it is that one.",
    },
    {
      type: "h2",
      id: "time",
      text: "When the problem is the hours",
    },
    {
      type: "verse",
      verse: {
        text: "So teach us to count our days, that we may gain a heart of wisdom.",
        ref: "Psalm 90:12",
      },
    },
    {
      type: "verse",
      verse: {
        text: "Therefore watch carefully how you walk, not as unwise, but as wise, redeeming the time, because the days are evil.",
        ref: "Ephesians 5:15–16",
      },
    },
    {
      type: "p",
      text: "“Redeeming the time” is a buying metaphor. It assumes the hours are already going somewhere and asks what you are getting for them — which is a far more answerable question than whether you are spending too long on your phone.",
    },
    {
      type: "h2",
      id: "using",
      text: "How to actually use these",
    },
    {
      type: "p",
      text: "A list of verses read once is a list of verses. Three things make the difference between reading these and them changing an evening:",
    },
    {
      type: "ol",
      items: [
        "Pick one, not twelve. The one that made you uncomfortable is the one. Twelve is a reading exercise; one is a practice.",
        "Attach it to the moment, not to the day. It belongs where the habit fires — the gap after dinner, the moment you get into bed — not in a morning slot where it competes with everything else.",
        "Let it arrive when you are not looking for it. A verse you have to remember to read is a verse you will read for four days.",
      ],
    },
    {
      type: "p",
      text: "That last point is most of why {Bible-Shield} works the way it does. The apps that take your evenings close at the hour you choose, and what stands between you and them is a short prayer with scripture in it — arriving at the exact moment the habit fires, rather than in a slot you have to remember. A verse a day also finds you on the home screen whether or not you open anything. For the practical side of the same problem, see {how-to-stop-scrolling-at-night}.",
      links: {
        "Bible-Shield": "/",
        "how-to-stop-scrolling-at-night":
          "/blog/how-to-stop-scrolling-at-night",
      },
    },
    {
      type: "p",
      text: "If a passage here is doing work for you, the neighbouring collections go deeper on one theme at a time: {ten-passages-on-self-control} takes the restraint question properly, and {verses-for-anxiety-at-night} is the one for the hours when the phone is the least useful thing in the room.",
      links: {
        "ten-passages-on-self-control":
          "/verses/bible-verses-about-self-control",
        "verses-for-anxiety-at-night":
          "/verses/bible-verses-for-anxiety-at-night",
      },
    },
  ],
  faq: [
    {
      q: "Does the Bible say anything about phones specifically?",
      a: "No, and any list that implies otherwise is stretching. What it addresses is older and more general: where attention goes, what has power over you, what rest is for, and what an hour is worth. Those cover the actual problem more precisely than a verse about technology ever could.",
    },
    {
      q: "Is phone addiction a sin?",
      a: "That framing usually makes things worse rather than better. 1 Corinthians 6:12 offers a more useful question than “is this permitted” — whether you are under its power. Shame is a poor engine for habit change and tends to produce secrecy rather than freedom.",
    },
    {
      q: "Which translation are these from?",
      a: "The World English Bible, a public-domain translation. Bible Shield ships several translations in the app, and any of them will serve for these passages.",
    },
  ],
  related: ["how-to-stop-scrolling-at-night", "christian-app-blocker-guide"],
};
